import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import toast from 'react-hot-toast';
import { Toaster } from 'react-hot-toast';
import Header from '../../components/ui/Header';
import AnalysisStatusIndicator from '../../components/ui/AnalysisStatusIndicator';
import PropertyInputSection from './components/PropertyInputSection';
import AnalysisTable from './components/AnalysisTable';
import { analyzeProperty } from '../../services/propertyAnalysis';
import { useAuth } from '../../contexts/AuthContext';
import { analysesService } from '../../services/supabaseService';
import GoBackButton from '../../components/GoBackButton';
import { useLanguage } from '../../contexts/LanguageContext';

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, userProfile, signOut } = useAuth();
  const { t } = useLanguage();
  const propertyInputRef = useRef(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisProgress, setAnalysisProgress] = useState(0);
  const [analyses, setAnalyses] = useState([]);
  const [loadingAnalyses, setLoadingAnalyses] = useState(true);

  const displayName = userProfile?.full_name || user?.user_metadata?.full_name || user?.email?.split('@')?.[0] || 'User';

  useEffect(() => {
    if (user?.id) {
      loadAnalyses();
      // Real-time subscription
      const channel = analysesService?.subscribeToUserAnalyses(user?.id, (payload) => {
        if (payload?.eventType === 'INSERT') {
          setAnalyses(prev => [payload?.new, ...prev]);
        }
      });
      return () => {
        if (channel) import('../../lib/supabase')?.then(m => m?.supabase?.removeChannel(channel));
      };
    } else {
      setLoadingAnalyses(false);
    }
  }, [user?.id]);

  const loadAnalyses = async () => {
    setLoadingAnalyses(true);
    const { data } = await analysesService?.getByUser(user?.id);
    setAnalyses(data || []);
    setLoadingAnalyses(false);
  };

  const handleAnalyze = async (input) => {
    setIsAnalyzing(true);
    setAnalysisProgress(0);

    const progressInterval = setInterval(() => {
      setAnalysisProgress(prev => {
        if (prev >= 85) { clearInterval(progressInterval); return 85; }
        return prev + 3;
      });
    }, 400);

    try {
      let propertyData = {};
      if (input?.type === 'manual') {
        propertyData = input?.value;
      } else if (input?.type === 'url') {
        propertyData = {
          address: input?.value,
          price: 'As listed',
          propertyType: 'House',
          region: input?.value?.includes('domain.com.au') || input?.value?.includes('realestate.com.au') ? 'Australia' : 'UAE',
          description: `Property listing from: ${input?.value}`
        };
      } else if (input?.type === 'file') {
        propertyData = {
          address: 'Property from uploaded screenshot',
          price: 'As shown in screenshot',
          propertyType: 'House',
          region: 'Australia',
          description: 'Property details from uploaded screenshot image'
        };
      }

      const result = await analyzeProperty(propertyData);
      clearInterval(progressInterval);
      setAnalysisProgress(100);

      const analysisEntry = {
        address: propertyData?.address || 'Property Analysis',
        suburb: propertyData?.region || 'Australia',
        region: propertyData?.region?.toLowerCase()?.includes('uae') ? 'uae' : 'australia',
        verdict: result?.verdict?.type ? (result?.verdict?.type?.charAt(0)?.toUpperCase() + result?.verdict?.type?.slice(1)) : 'Fair',
        confidence_score: result?.confidence?.overall || 80,
        portal: input?.type === 'url' ? (input?.value?.includes('domain') ? 'Domain.com.au' : 'Realestate.com.au') : 'Manual Entry',
        data: result
      };

      if (user?.id) {
        await analysesService?.create(user?.id, analysisEntry);
      } else {
        setAnalyses(prev => [{ ...analysisEntry, id: Date.now(), created_at: new Date()?.toISOString() }, ...prev]);
      }

      setTimeout(() => {
        setIsAnalyzing(false);
        setAnalysisProgress(0);
        navigate('/analysis-detail', { state: { analysis: result } });
      }, 800);
    } catch (error) {
      clearInterval(progressInterval);
      setIsAnalyzing(false);
      setAnalysisProgress(0);
      toast?.error('Analysis failed. Please check your property details and try again.');
    }
  };

  const handleViewDetails = (analysisId) => {
    navigate('/analysis-detail', { state: { analysisId } });
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/authentication');
  };

  const handleStartFirstAnalysis = () => {
    if (propertyInputRef?.current) {
      propertyInputRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const headerUser = user ? {
    id: user?.id,
    name: displayName,
    email: user?.email,
    role: userProfile?.role || 'buyer'
  } : null;

  return (
    <>
      <Toaster position="top-right" />
      <Helmet>
        <title>Dashboard - ListingLens | AI Property Analysis</title>
        <meta name="description" content="Analyze Australian property listings with AI-powered insights." />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header user={headerUser} onLogout={handleLogout} />

        <main className="main-content">
          <div className="max-w-[1440px] mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-10">
            <div className="mb-6 md:mb-8">
              <div className="mb-3">
                <GoBackButton fallbackPath="/" label="Go Back" />
              </div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground font-heading mb-2">
                {t('welcomeBack2')}, {displayName?.split(' ')?.[0]}
              </h1>
              <p className="text-sm md:text-base text-muted-foreground font-body">
                {t('dashboardSubtitle')}
              </p>
            </div>

            <div className="space-y-6 md:space-y-8">
              <div ref={propertyInputRef}>
                <PropertyInputSection onAnalyze={handleAnalyze} isAnalyzing={isAnalyzing} />
              </div>

              {isAnalyzing && (
                <AnalysisStatusIndicator
                  status="processing"
                  progress={analysisProgress}
                  message="Our AI is analyzing the property listing, extracting key details, and comparing market data to provide you with comprehensive insights."
                  estimatedTime="15-30 seconds"
                />
              )}

              {isAnalyzing && (
                <div className="space-y-3">
                  <p className="text-sm text-muted-foreground font-medium">{t('preparingResults')}</p>
                  {[...Array(3)]?.map((_, i) => (
                    <div key={i} className="rounded-xl border border-border bg-card p-4 animate-pulse" style={{ opacity: 1 - i * 0.2 }}>
                      <div className="flex items-start gap-4">
                        <div className="w-16 h-16 rounded-lg bg-muted flex-shrink-0" />
                        <div className="flex-1 space-y-2">
                          <div className="h-4 bg-muted rounded w-3/4" />
                          <div className="h-3 bg-muted rounded w-1/2" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <AnalysisTable
                analyses={analyses}
                onViewDetails={handleViewDetails}
                onStartAnalysis={handleStartFirstAnalysis}
                loading={loadingAnalyses}
              />
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default Dashboard;